<template>
  <registry-page
    title="Проводки"
    description="Журнал бухгалтерских проводок за выбранный период."
  >
    <template #metadata>
      Период: {{ formatDate(applied.period_start, '—') }} — {{ formatDate(applied.period_end, '—') }}
    </template>
    <template #actions>
      <q-btn
        flat no-caps icon="download" label="Экспорт CSV"
        :disable="visibleRows.length === 0"
        data-testid="transfers-export"
        @click="exportRows"
      />
      <q-btn flat no-caps icon="refresh" label="Обновить" :loading="loading" @click="loadTransfers" />
    </template>

    <q-banner v-if="validationError" rounded class="bg-negative text-white q-mb-md" role="alert">
      {{ validationError }}
    </q-banner>

    <app-data-table
      title="Операции"
      :count="pagination.rowsNumber"
      :rows="visibleRows"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-title="stateTitle"
      :state-message="stateMessage"
      storage-key="finance-transfers"
      data-testid="transfers-table"
      @request="onRequest"
      @retry="loadTransfers"
      @reset="resetFilters"
      @row-click="openTransferDocument"
    >
      <template #filters>
        <filter-bar
          ref="filterBar"
          v-model="search"
          search-label="Операция, документ, счет или пользователь"
          search-test-id="transfers-search"
          :has-filters="hasFilters"
          :applied-filters="appliedFilters"
          @search="syncSearch"
          @reset="resetFilters"
          @remove="removeFilter"
        >
          <template #quick>
            <q-input v-model="draft.period_start" dense outlined label="Начало периода" :mask="dateInputMask" class="transfers-list__period-filter" />
            <q-input v-model="draft.period_end" dense outlined label="Конец периода" :mask="dateInputMask" class="transfers-list__period-filter" />
            <q-select v-model="draft.status" dense outlined emit-value map-options clearable label="Статус" :options="statusOptions" class="transfers-list__status-filter" />
            <q-btn no-caps outline color="primary" label="Применить" :disable="Boolean(validationError)" @click="applyFilters" />
          </template>
          <template #advanced>
            <div class="column q-gutter-md">
              <q-input v-model="draft.period_start" dense outlined label="Начало периода" :mask="dateInputMask" />
              <q-input v-model="draft.period_end" dense outlined label="Конец периода" :mask="dateInputMask" />
              <q-select v-model="draft.status" dense outlined emit-value map-options clearable label="Статус" :options="statusOptions" />
              <q-input v-model="draft.bill_id" dense outlined clearable label="Счет или ID счета" />
              <q-btn no-caps color="primary" label="Применить фильтры" :disable="Boolean(validationError)" @click="applyFilters" />
            </div>
          </template>
        </filter-bar>
      </template>

      <template #body-cell-document="props">
        <q-td :props="props">
          <router-link
            v-if="documentRoute(props.row)"
            class="transfers-list__document-link"
            :to="documentRoute(props.row)"
            @click.stop
          >
            {{ props.row.document }}
          </router-link>
          <span v-else>{{ props.row.document }}</span>
        </q-td>
      </template>
      <template #body-cell-amount="props">
        <q-td :props="props"><amount-cell :value="props.value" /></q-td>
      </template>
      <template #body-cell-status="props">
        <q-td :props="props">
          <status-badge
            :label="statusLabel(props.value)"
            :tone="statusTone(props.value)"
            :icon="statusIcon(props.value)"
          />
        </q-td>
      </template>
      <template #body-cell-actions="props">
        <q-td :props="props">
          <q-btn
            flat round dense icon="info"
            :aria-label="`Подробнее о проводке ${props.row.name || props.row.id}`"
            @click.stop="showDetails(props.row)"
          />
        </q-td>
      </template>
    </app-data-table>

    <q-dialog v-model="detailsOpen" position="right">
      <q-card class="transfers-list__details">
        <q-card-section class="row items-center">
          <div>
            <div class="text-h6">{{ selectedTransfer ? selectedTransfer.name : 'Проводка' }}</div>
            <div class="text-caption text-grey-7">{{ selectedTransfer ? selectedTransfer.date_display : '' }}</div>
          </div>
          <q-space />
          <q-btn flat round icon="close" aria-label="Закрыть" v-close-popup />
        </q-card-section>
        <q-separator />
        <q-list v-if="selectedTransfer" separator>
          <q-item><q-item-section><q-item-label caption>Дебет</q-item-label><q-item-label>{{ selectedTransfer.debit_account }}</q-item-label></q-item-section></q-item>
          <q-item><q-item-section><q-item-label caption>Кредит</q-item-label><q-item-label>{{ selectedTransfer.credit_account }}</q-item-label></q-item-section></q-item>
          <q-item><q-item-section><q-item-label caption>Сумма</q-item-label><q-item-label><amount-cell :value="selectedTransfer.amount" /></q-item-label></q-item-section></q-item>
          <q-item><q-item-section><q-item-label caption>Документ</q-item-label><q-item-label>{{ selectedTransfer.document }}</q-item-label></q-item-section></q-item>
          <q-item><q-item-section><q-item-label caption>Пользователь</q-item-label><q-item-label>{{ selectedTransfer.user_display }}</q-item-label></q-item-section></q-item>
          <q-item><q-item-section><q-item-label caption>Комментарий</q-item-label><q-item-label>{{ selectedTransfer.comment || '—' }}</q-item-label></q-item-section></q-item>
        </q-list>
      </q-card>
    </q-dialog>
  </registry-page>
</template>

<script>
import moment from 'moment'
import AmountCell from '../../../components/ui/AmountCell.vue'
import AppDataTable from '../../../components/ui/AppDataTable.vue'
import FilterBar from '../../../components/ui/FilterBar.vue'
import RegistryPage from '../../../components/ui/RegistryPage.vue'
import StatusBadge from '../../../components/ui/StatusBadge.vue'
import {
  DATE_DISPLAY_FORMAT,
  DATE_INPUT_MASK,
  formatDate,
  parseDate,
  toIsoDate
} from '../../../services/dateFormat'
import { downloadCsv } from '../../../services/financeCore'
import { normalizeTransfers, transferDocumentRoute } from '../../../services/transferWorkspace'

const columns = Object.freeze([
  { name: 'date', align: 'left', label: 'Дата', field: 'date_display', sortable: true, required: true },
  { name: 'name', align: 'left', label: 'Операция', field: (row) => row.name || 'Без названия', required: true },
  { name: 'document', align: 'left', label: 'Документ', field: 'document', required: true },
  { name: 'debit_account', align: 'left', label: 'Дебет', field: 'debit_account' },
  { name: 'credit_account', align: 'left', label: 'Кредит', field: 'credit_account' },
  { name: 'amount', align: 'right', label: 'Сумма', field: 'amount', sortable: true, required: true },
  { name: 'currency', align: 'left', label: 'Валюта', field: (row) => row.currency || 'RUB', classes: 'column-priority-secondary' },
  { name: 'status', align: 'left', label: 'Статус', field: 'status' },
  { name: 'template', align: 'left', label: 'Шаблон', field: (row) => row.template_name || '—', classes: 'column-priority-secondary' },
  { name: 'user', align: 'left', label: 'Пользователь', field: 'user_display', classes: 'column-priority-secondary' },
  { name: 'actions', align: 'right', label: 'Действия', field: 'id', required: true }
])

const statusOptions = Object.freeze([
  { label: 'Черновик', value: 'draft' },
  { label: 'Проведено', value: 'posted' },
  { label: 'Отменено', value: 'cancelled' },
  { label: 'Архив', value: 'archived' }
])

function normalizedDate(value, fallback) {
  const parsed = parseDate(value)
  return parsed.isValid() ? parsed.format(DATE_DISPLAY_FORMAT) : fallback
}

function defaultApplied(storePeriod, query = {}) {
  const fallbackStart = moment().startOf('quarter').format(DATE_DISPLAY_FORMAT)
  const fallbackEnd = moment().endOf('quarter').format(DATE_DISPLAY_FORMAT)
  return {
    period_start: normalizedDate(query.from || storePeriod?.from, fallbackStart),
    period_end: normalizedDate(query.to || storePeriod?.to, fallbackEnd),
    status: query.status || null,
    bill_id: query.account || null
  }
}

export default {
  name: 'transfers_list',
  components: { AmountCell, AppDataTable, FilterBar, RegistryPage, StatusBadge },
  data() {
    const applied = defaultApplied(this.$store.state.filter_period, this.$route.query)
    return {
      columns,
      statusOptions,
      dateInputMask: DATE_INPUT_MASK,
      rows: [],
      search: String(this.$route.query.search || ''),
      applied,
      draft: { ...applied },
      pagination: {
        sortBy: 'date',
        descending: true,
        page: Math.max(1, Number(this.$route.query.page || 1)),
        rowsPerPage: Math.max(1, Number(this.$route.query.size || 50)),
        rowsNumber: 0
      },
      loading: true,
      error: '',
      errorState: 'error',
      selectedTransfer: null,
      detailsOpen: false,
      handlers: [],
      routeSignature: ''
    }
  },
  computed: {
    validationError() {
      if (!this.draft.period_start || !this.draft.period_end) return 'Укажите начало и конец периода.'
      const start = parseDate(this.draft.period_start)
      const end = parseDate(this.draft.period_end)
      if (!start.isValid() || !end.isValid()) return 'Проверьте формат дат периода.'
      if (start.isAfter(end)) return 'Дата начала не может быть позже даты окончания.'
      return ''
    },
    visibleRows() {
      const query = this.search.trim().toLocaleLowerCase('ru-RU')
      if (!query) return this.rows
      return this.rows.filter((row) => [
        row.name, row.document, row.debit_account, row.credit_account, row.template_name, row.user_display
      ].some((value) => String(value || '').toLocaleLowerCase('ru-RU').includes(query)))
    },
    hasFilters() {
      const defaults = defaultApplied(this.$store.state.filter_period)
      return Boolean(
        this.search || this.applied.status || this.applied.bill_id ||
        this.applied.period_start !== defaults.period_start || this.applied.period_end !== defaults.period_end
      )
    },
    appliedFilters() {
      const filters = [{
        key: 'period',
        label: `Период: ${this.applied.period_start} — ${this.applied.period_end}`
      }]
      if (this.search) filters.push({ key: 'search', label: `Поиск: ${this.search}` })
      if (this.applied.status) filters.push({ key: 'status', label: `Статус: ${this.statusLabel(this.applied.status)}` })
      if (this.applied.bill_id) filters.push({ key: 'account', label: `Счет: ${this.applied.bill_id}` })
      return filters
    },
    tableState() {
      if (this.loading) return 'loading'
      if (this.error) return this.errorState
      if (!this.rows.length) return this.hasFilters ? 'filtered-empty' : 'empty'
      if (!this.visibleRows.length) return 'filtered-empty'
      return 'ready'
    },
    stateTitle() {
      if (this.error) return 'Не удалось загрузить проводки'
      return this.hasFilters ? 'Проводки не найдены' : 'Проводок пока нет'
    },
    stateMessage() {
      return this.error || (this.hasFilters ? 'Измените или сбросьте фильтры.' : 'За выбранный период операций нет.')
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(query) {
        const signature = JSON.stringify(query)
        if (signature === this.routeSignature) return
        this.routeSignature = signature
        const previousServerState = JSON.stringify({
          applied: this.applied,
          page: this.pagination.page,
          size: this.pagination.rowsPerPage
        })
        const applied = defaultApplied(this.$store.state.filter_period, query)
        this.applied = applied
        this.draft = { ...applied }
        this.search = String(query.search || '')
        this.pagination.page = Math.max(1, Number(query.page || 1))
        this.pagination.rowsPerPage = Math.max(1, Number(query.size || 50))
        const nextServerState = JSON.stringify({
          applied: this.applied,
          page: this.pagination.page,
          size: this.pagination.rowsPerPage
        })
        if (previousServerState !== nextServerState && this.connection.isAuthenticated) this.loadTransfers()
      }
    }
  },
  mounted() {
    this.routeSignature = JSON.stringify(this.$route.query)
    this.listen('onListTransfers', this.onTransfers)
    this.listen('connectionReady', this.loadTransfers)
    if (this.connection.isAuthenticated) this.loadTransfers()
  },
  beforeUnmount() {
    this.handlers.forEach(([event, handler]) => this.emitter.off(event, handler))
  },
  methods: {
    formatDate,
    documentRoute: transferDocumentRoute,
    listen(event, handler) {
      this.emitter.on(event, handler)
      this.handlers.push([event, handler])
    },
    loadTransfers() {
      if (parseDate(this.applied.period_start).isAfter(parseDate(this.applied.period_end))) return
      this.loading = true
      this.error = ''
      this.connection.listTransfers('transfers-list', {
        period_start: toIsoDate(this.applied.period_start),
        period_end: toIsoDate(this.applied.period_end),
        status: this.applied.status || undefined,
        bill_id: this.applied.bill_id || undefined,
        page: this.pagination.page,
        page_size: this.pagination.rowsPerPage
      })
    },
    onTransfers(response) {
      this.loading = false
      const normalized = normalizeTransfers(response)
      if (!normalized.ok) {
        this.errorState = normalized.state || 'error'
        this.error = normalized.message
        return
      }
      this.rows = normalized.rows
      this.pagination.rowsNumber = Number(normalized.pagination.total ?? normalized.rows.length)
      this.pagination.page = Number(normalized.pagination.page ?? this.pagination.page)
      this.pagination.rowsPerPage = Number(normalized.pagination.page_size ?? this.pagination.rowsPerPage)
    },
    async applyFilters() {
      if (this.validationError) return
      this.applied = { ...this.draft }
      this.pagination.page = 1
      if (this.$refs.filterBar) this.$refs.filterBar.advancedOpen = false
      await this.updateRoute(true)
      this.loadTransfers()
    },
    async updateRoute(push = false) {
      const defaults = defaultApplied(this.$store.state.filter_period)
      const query = {
        ...(this.search ? { search: this.search } : {}),
        ...(this.applied.period_start !== defaults.period_start ? { from: toIsoDate(this.applied.period_start) } : {}),
        ...(this.applied.period_end !== defaults.period_end ? { to: toIsoDate(this.applied.period_end) } : {}),
        ...(this.applied.status ? { status: this.applied.status } : {}),
        ...(this.applied.bill_id ? { account: this.applied.bill_id } : {}),
        ...(this.pagination.page > 1 ? { page: String(this.pagination.page) } : {}),
        ...(this.pagination.rowsPerPage !== 50 ? { size: String(this.pagination.rowsPerPage) } : {})
      }
      const current = JSON.stringify(this.$route.query)
      const next = JSON.stringify(query)
      if (current === next) {
        return false
      }
      await this.$router[push ? 'push' : 'replace']({ query })
      return true
    },
    syncSearch() {
      this.updateRoute(false)
    },
    async resetFilters() {
      const defaults = defaultApplied(this.$store.state.filter_period)
      this.search = ''
      this.applied = { ...defaults }
      this.draft = { ...defaults }
      this.pagination.page = 1
      await this.updateRoute(true)
      this.loadTransfers()
    },
    async removeFilter(key) {
      if (key === 'search') this.search = ''
      if (key === 'status') this.applied.status = this.draft.status = null
      if (key === 'account') this.applied.bill_id = this.draft.bill_id = null
      if (key === 'period') {
        const defaults = defaultApplied(this.$store.state.filter_period)
        this.applied.period_start = this.draft.period_start = defaults.period_start
        this.applied.period_end = this.draft.period_end = defaults.period_end
      }
      this.pagination.page = 1
      await this.updateRoute(true)
      this.loadTransfers()
    },
    async onRequest(props) {
      this.pagination = { ...this.pagination, ...props.pagination }
      await this.updateRoute(false)
      this.loadTransfers()
    },
    statusLabel(status) {
      return statusOptions.find((option) => option.value === status)?.label || 'Не указан'
    },
    statusTone(status) {
      if (status === 'posted') return 'success'
      if (status === 'cancelled' || status === 'archived') return 'danger'
      return 'neutral'
    },
    statusIcon(status) {
      if (status === 'posted') return 'check_circle'
      if (status === 'cancelled' || status === 'archived') return 'cancel'
      return 'edit_note'
    },
    openTransferDocument(event, row) {
      if (event?.defaultPrevented) return
      const route = transferDocumentRoute(row)
      if (route) this.$router.push(route)
      else this.showDetails(row)
    },
    showDetails(row) {
      this.selectedTransfer = row
      this.detailsOpen = true
    },
    exportRows() {
      downloadCsv('transfers.csv', [
        { name: 'date_display', label: 'Дата' },
        { name: 'name', label: 'Операция' },
        { name: 'document', label: 'Документ' },
        { name: 'debit_account', label: 'Дебет' },
        { name: 'credit_account', label: 'Кредит' },
        { name: 'amount', label: 'Сумма' },
        { name: 'currency', label: 'Валюта' },
        { name: 'user_display', label: 'Пользователь' }
      ], this.visibleRows)
    }
  }
}
</script>

<style scoped>
.transfers-list__document-link { color: var(--sid-color-primary); font-weight: var(--sid-font-weight-medium); }
.transfers-list__details { width: min(28rem, 94vw); min-height: 100vh; }
.transfers-list__period-filter { width: 10rem; }
.transfers-list__status-filter { min-width: 10rem; }
</style>
